/**
 * @file        src/lib/confirm.js
 * @description Imperative, themed confirm / prompt dialog that replaces the browser's
 *              native window.confirm / window.prompt so destructive actions use the
 *              app's own UI (matching the toast infra). Renders via <ConfirmHost />.
 *
 *              confirm()    → Promise<boolean>            (OK = true, Cancel/Esc = false)
 *              promptText() → Promise<string|null>        (OK = value, Cancel/Esc = null)
 *
 *              Each accepts either a string (used as the message) or an options object:
 *                { title, message, confirmLabel, cancelLabel, danger, placeholder,
 *                  defaultValue }
 * @author      Kinetika Labs
 * @created     2026-07-23
 * @changelog
 *   - 2026-07-23 Claude — Initial (replace native confirm/alert/prompt popups with
 *                          an on-theme modal).
 */
import { create } from 'zustand';

export const useConfirmDialog = create((set, get) => ({
  dialog: null,
  request(opts) {
    // Never leave a previous promise hanging if a second dialog is opened.
    const prev = get().dialog;
    if (prev) prev.resolve(prev.kind === 'prompt' ? null : false);
    return new Promise((resolve) => {
      set({
        dialog: {
          kind: 'confirm',
          danger: true,
          confirmLabel: 'Delete',
          cancelLabel: 'Cancel',
          ...opts,
          resolve,
        },
      });
    });
  },
  settle(result) {
    const d = get().dialog;
    if (!d) return;
    set({ dialog: null });
    d.resolve(result);
  },
}));

function normalize(arg) {
  return typeof arg === 'string' ? { message: arg } : (arg || {});
}

/** Ask the user to confirm a (usually destructive) action. Resolves boolean. */
export function confirm(arg) {
  return useConfirmDialog.getState().request({ kind: 'confirm', ...normalize(arg) });
}

/** Ask the user for a single line of text. Resolves the string, or null if cancelled. */
export function promptText(arg) {
  return useConfirmDialog.getState().request({
    kind: 'prompt',
    danger: false,
    confirmLabel: 'OK',
    ...normalize(arg),
  });
}
